// The Library. Everyone reads; managers and admins curate (spec: 06-library.md).
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api';
import { documents, type Attachment, type LibrarySection } from '../documentsApi';
import { useSession } from '../session';
import { Banner } from '../ui';
import { DocumentList } from '../components/Documents';
import { FileDrop } from '../components/ui/FileDrop';
import { DocumentViewer } from '../components/DocumentViewer';
import { uploadAttachment } from '../documentsApi';
import { useLatestRequest } from '../lib/useLatestRequest';

export function LibraryPage() {
  const { user } = useSession();
  const [sections, setSections] = useState<LibrarySection[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Attachment | null>(null);
  const [newSection, setNewSection] = useState('');

  const isManager = user?.role === 'manager' || user?.role === 'admin';

  const begin = useLatestRequest();

  const load = useCallback(async () => {
    // Typed-into search: only the newest answer may land. See
    // `useLatestRequest`.
    const isCurrent = begin();
    try {
      const result = await documents.library(search || undefined);
      if (!isCurrent()) return;
      setSections(result.sections);
      setError(null);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof ApiError ? err.message : 'Could not load the Library.');
    }
  }, [begin, search]);

  useEffect(() => {
    void load();
  }, [load]);

  async function addSection(event: React.FormEvent) {
    event.preventDefault();
    if (newSection.trim() === '') return;
    try {
      await documents.createSection(newSection.trim());
      setNewSection('');
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that section.');
    }
  }

  async function removeSection(section: LibrarySection) {
    if (!window.confirm(`Delete the “${section.name}” section?`)) return;
    try {
      await documents.deleteSection(section.id);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete that section.');
    }
  }

  async function removeDocument(attachment: Attachment) {
    if (!window.confirm(`Remove “${attachment.title}”? An admin can get it back for 30 days.`)) {
      return;
    }
    try {
      await documents.remove(attachment.id);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that.');
    }
  }

  return (
    <>
      {/* Not PageHeader: the action here is a form field, and PageHeader's
          actions sit in their own flex box, which stopped the search box
          wrapping onto its own line at 375px and squeezed it from 256px to
          209px. The `.row` keeps that wrap; the margin moves onto the row and
          off the heading so the field no longer sits hard on the panel. */}
      <div className="row mb-5">
        <h1 className="!mb-0">Library</h1>
        <span className="spacer" />
        <label style={{ flex: '0 1 16rem', marginBottom: 0 }}>
          Search
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or description…"
          />
        </label>
      </div>

      {error && <Banner kind="error">{error}</Banner>}

      {isManager && (
        <section className="panel">
          <form onSubmit={addSection} noValidate>
            <div className="row">
              <label style={{ flex: '1 1 16rem' }}>
                New section
                <input
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value)}
                  placeholder="Policies, SWMS templates, Toolbox talks…"
                />
              </label>
              <button type="submit">Add section</button>
            </div>
            <p className="hint">
              One level, no folders inside folders: a document lives in exactly one section.
            </p>
          </form>
        </section>
      )}

      {sections === null ? (
        <p className="muted">Loading…</p>
      ) : sections.length === 0 ? (
        <section className="panel">
          <p className="muted">
            {isManager
              ? 'Nothing here yet. Add a section, then put your policies and SWMS templates in it.'
              : 'Nothing here yet. Your office will add the company documents.'}
          </p>
        </section>
      ) : (
        sections.map((section) => (
          <CollapsibleSection
            key={section.id}
            section={section}
            search={search}
            isManager={isManager}
            onDeleteSection={removeSection}
            onUploaded={load}
            onUploadError={setError}
            onOpen={setViewing}
            onRemoveDocument={removeDocument}
          />
        ))
      )}

      {viewing && <DocumentViewer attachment={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

/**
 * One section, collapsed until wanted (2026-09-14, Nathan's dictation after
 * loading fifty SWPs into one section made the page a scroll marathon).
 *
 * COLLAPSED IS THE DEFAULT: the page opens as an index of section names with
 * counts, which is the navigation a big library needs — a small one pays one
 * tap. The header is the toggle (ZT's NavGroup behaviour; unlike the
 * sidebar's port, this heading was never a link, so there is nothing to
 * preserve by confining the toggle to the chevron). Delete section sits
 * BESIDE the toggle button rather than inside it — buttons cannot nest, and
 * a sibling needs no stopPropagation.
 *
 * SEARCHING OVERRIDES THE FOLD. The server answers a search with every
 * section, matches filtered inside each — so a section with matches opens
 * itself and one without stays shut showing 0, rather than results hiding
 * behind closed folds (a search whose hits are invisible reads as broken).
 * A manual toggle wins until the search text next changes, the same
 * manual-then-auto arbitration NavGroup runs against navigation.
 */
function CollapsibleSection({
  section,
  search,
  isManager,
  onDeleteSection,
  onUploaded,
  onUploadError,
  onOpen,
  onRemoveDocument,
}: {
  section: LibrarySection;
  search: string;
  isManager: boolean;
  onDeleteSection: (section: LibrarySection) => void;
  onUploaded: () => void;
  onUploadError: (message: string) => void;
  onOpen: (attachment: Attachment) => void;
  onRemoveDocument: (attachment: Attachment) => void;
}) {
  const [manual, setManual] = useState<boolean | null>(null);
  const searching = search.trim() !== '';
  const open = manual ?? (searching && section.documents.length > 0);

  useEffect(() => {
    setManual(null);
  }, [search]);

  const count = section.documents.length;

  return (
    <section className="panel">
      <div className="row">
        {/* The heading lives INSIDE the button so the whole row is the
            44px tap target, not just the chevron. */}
        <button
          type="button"
          onClick={() => setManual(!open)}
          aria-expanded={open}
          className="-m-2 flex min-h-[2.75rem] flex-1 items-center gap-2 rounded p-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
        >
          <svg
            className={`h-4 w-4 flex-none text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <h2 className="!mb-0">{section.name}</h2>
          <span className="muted text-sm">
            {count === 1 ? '1 document' : `${count} documents`}
          </span>
        </button>
        {isManager && count === 0 && (
          <button className="link" onClick={() => void onDeleteSection(section)}>
            Delete section
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3">
          {isManager && (
            <SectionUpload sectionId={section.id} onUploaded={onUploaded} onError={onUploadError} />
          )}

          <DocumentList
            documents={section.documents}
            emptyMessage="Nothing in this section yet."
            onOpen={onOpen}
            onRemove={onRemoveDocument}
            canRemove={() => isManager}
          />
        </div>
      )}
    </section>
  );
}

function SectionUpload({
  sectionId,
  onUploaded,
  onError,
}: {
  sectionId: string;
  onUploaded: () => void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function send(files: File[]) {
    if (files.length === 0) return;
    setBusy(true);
    for (const file of files) {
      try {
        await uploadAttachment({ kind: 'library', ownerId: sectionId, file });
      } catch (err) {
        onError(err instanceof ApiError ? err.message : `Could not upload ${file.name}.`);
      }
    }
    setBusy(false);
    onUploaded();
  }

  return (
    <div className="upload-box">
      {/* Drop or browse (round 45), through the shared FileDrop. */}
      <FileDrop
        id={`lib-${sectionId}`}
        multiple
        disabled={busy}
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx"
        typeLabel="PDF, images, Word and Excel"
        label={busy ? 'Uploading…' : undefined}
        hint="Add documents to this section."
        onFiles={(files) => void send(files)}
        onReject={onError}
      />
    </div>
  );
}
